import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import {
  continueLabel, FOUND_BY_BOTH_LABEL, isFoundByBoth, UNVERIFIED_QUOTE_LABEL, UNVERIFIED_SECTION_LABEL, STALE_RUN_TEXT, isStaleRunning, isUnverifiedSection, bannerText, currentReviewTotals, isUnverifiedQuote, toDateValue, formBadge, groupFindings, missingFormLine,
  type ReviewFormDoc,
} from '../../features/crf/protocolReview';
import type { ReviewFormRecord, ReviewSummary } from '../../features/crf/useProtocolReview';

function formatRunDate(summary: ReviewSummary): string {
  const t = summary.completedAt ?? summary.startedAt;
  const d = toDateValue(t);
  return d ? d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'date unknown';
}

/** Summary banner above the form list. Renders nothing until a review exists. */
export function ProtocolReviewBanner({ summary, reviewForms, currentFormIds, stillRunningNote, onContinue, continueDisabled }: {
  onContinue?: () => void;
  continueDisabled?: boolean;
  summary: ReviewSummary | null;
  reviewForms: Record<string, ReviewFormRecord>;
  currentFormIds: string[];
  stillRunningNote: boolean;
}) {
  if (!summary && !stillRunningNote) return null;
  if (!summary) {
    return (
      <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
        The review is still running on the server.
      </div>
    );
  }
  const totals = currentReviewTotals(reviewForms, summary.runId, currentFormIds);
  const shown = summary.status === 'complete' || summary.status === 'partial' ?{ ...summary, findingCount: totals.items } : summary;
  const stale = isStaleRunning(summary, new Date());
  const text = stale ? STALE_RUN_TEXT : bannerText(shown, totals.forms, formatRunDate(summary), totals.notReviewed);
  const failed = summary.status === 'failed' || stale;
  const running = summary.status === 'running' && !stale;
  const missing = summary.status === 'complete' ? summary.missingForms ?? [] : [];
  return (
    <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${failed ? 'border-red-200 bg-red-50 text-red-800' : running ? 'border-blue-200 bg-blue-50 text-blue-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
      <div className="flex items-center gap-2">
        {running ? <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" /> : <AlertTriangle className="h-4 w-4 flex-shrink-0" />}
        <span>{text}</span>
      </div>
      {summary.status === 'partial' && !stale && (
        <button
          type="button"
          onClick={onContinue}
          disabled={continueDisabled}
          className="mt-2 text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5"
        >
          {continueDisabled && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {continueLabel(summary)}
        </button>
      )}
      {summary.missingFormsError && (
        <p className="mt-1 text-xs">The check for missing forms could not run: {summary.missingFormsError}</p>
      )}
      {stillRunningNote && running && <p className="mt-1 text-xs">The review is still running on the server.</p>}
      {missing.length > 0 && (
        <div className="mt-2 text-xs">
          <p className="font-semibold">Forms the protocol may need:</p>
          <ul className="list-disc ml-5 mt-0.5 space-y-0.5">
            {missing.map((m, i) => (
              <li key={`${m.name}-${i}`}>
                {missingFormLine(m)}
                {m.protocolQuote && !isUnverifiedQuote({ protocolQuote: m.protocolQuote, quoteVerified: m.quoteVerified !== false }) && <span className="italic text-amber-800"> &ldquo;{m.protocolQuote}&rdquo;</span>}
                {isUnverifiedQuote({ protocolQuote: m.protocolQuote, quoteVerified: m.quoteVerified !== false }) && <span className="text-amber-700"> ({UNVERIFIED_QUOTE_LABEL})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-1 text-xs opacity-80">These are items to check. They do not block approval.</p>
    </div>
  );
}

/** Small count chip for a form card header. Renders nothing when there is nothing to show. */
export function ProtocolReviewBadge({ doc, summaryRunId, onClick }: {
  doc: ReviewFormDoc | undefined;
  summaryRunId: string | undefined;
  onClick?: () => void;
}) {
  const badge = formBadge(doc, summaryRunId);
  if (!badge) return null;
  const tone = badge.tone === 'red' ? 'bg-red-100 text-red-700' : badge.tone === 'gray' || badge.stale ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-800';
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${tone} ${onClick ? 'cursor-pointer' : ''}`}
      title={badge.title}
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
    >
      {badge.text}
    </span>
  );
}

/** Findings for one form, inside its expanded card. Collapsed by default. */
export function ProtocolReviewFindings({ record, summaryRunId }: {
  record: ReviewFormRecord | undefined;
  summaryRunId: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  if (!record) return null;
  const stale = !!summaryRunId && record.runId !== summaryRunId;
  if (record.status === 'failed') {
    return (
      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
        Review failed{stale ? ' (from an earlier review)' : ''}{record.reason ? `: ${record.reason}` : ''}
      </div>
    );
  }
  if (record.status === 'skipped') {
    return (
      <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
        Not reviewed{stale ? ' (from an earlier review)' : ''}{record.reason ? `: ${record.reason}` : ''}
      </div>
    );
  }
  if (record.status === 'reviewed' && record.findings.length === 0 && record.passNote) {
    return <p className="mb-4 text-xs text-gray-500">{record.passNote}</p>;
  }
  if (record.status !== 'reviewed' || record.findings.length === 0) return null;
  const groups = groupFindings(record.findings);
  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-900 text-left"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Protocol review: {record.findings.length} {record.findings.length === 1 ? 'item' : 'items'} to check
        {stale && <span className="text-xs font-normal text-gray-600">(from an earlier review)</span>}
      </button>
      {record.passNote && <p className="px-3 pb-2 text-xs text-gray-500">{record.passNote}</p>}
      {open && (
        <div className="px-3 pb-3 space-y-3">
          {groups.map((g) => (
            <div key={g.kind}>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">{g.label}</p>
              <ul className="mt-1 space-y-2">
                {g.findings.map((f) => (
                  <li key={f.id} className="text-sm text-gray-800">
                    {f.fieldVarName && <code className="font-mono text-xs bg-white border border-gray-200 rounded px-1 mr-1.5">{f.fieldVarName}</code>}
                    {f.message}
                    {isFoundByBoth(f) && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-medium whitespace-nowrap">{FOUND_BY_BOTH_LABEL}</span>}
                    {f.protocolSection && <span className="text-gray-500"> Protocol section {f.protocolSection}{isUnverifiedSection(f) ? <span className="text-amber-700"> ({UNVERIFIED_SECTION_LABEL})</span> : null}.</span>}
                    {f.protocolQuote && (
                      <blockquote className="mt-0.5 border-l-2 border-gray-300 pl-2 text-xs italic text-gray-600">
                        &ldquo;{f.protocolQuote}&rdquo;
                        {isUnverifiedQuote(f) && <span className="not-italic text-amber-700"> ({UNVERIFIED_QUOTE_LABEL})</span>}
                      </blockquote>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
